import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CurrencyDollar, CheckCircle, XCircle, WarningOctagon } from "@phosphor-icons/react";

/**
 * v3.2.32 · Admin refund queue. Lists pending refund requests + shows
 * approve/deny actions. Approved requests fire a Stripe refund which
 * the `charge.refunded` webhook then finalizes (ledger + milestone
 * reversal + PE notification).
 */
export default function AdminRefundsPage() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [decideModal, setDecideModal] = useState(null);  // { row, action }

  const load = async (statusFilter) => {
    setBusy("load");
    try {
      const { data } = await api.get("/admin/refund-requests",
        { params: { status: statusFilter } });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setBusy(""); }
  };
  useEffect(() => { load(tab); }, [tab]);

  const decide = async () => {
    if (!decideModal) return;
    const { row, action } = decideModal;
    setBusy(row.id);
    try {
      await api.post(`/admin/refund-requests/${row.id}/${action}`,
        { admin_note: note });
      toast.success(action === "approve" ? "Refund approved & processed." : "Request denied.");
      setDecideModal(null);
      setNote("");
      load(tab);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `${action} failed`);
    } finally { setBusy(""); }
  };

  const badge = (status) => {
    if (status === "approved") return "border-emerald-400/40 text-emerald-400 bg-emerald-400/10";
    if (status === "denied")   return "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/10";
    return "border-[#FFCC00]/40 text-[#FFCC00] bg-[#FFCC00]/10";
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="admin-refunds" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// Admin</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="admin-refunds-heading">
          Refund Requests
        </h1>
        <p className="text-gray-400 mt-2 max-w-2xl">
          Client refund requests that fall outside the 24h self-serve window.
          Approving fires the Stripe refund; the PE ledger is reversed automatically.
        </p>

        <div className="mt-6 flex gap-2 border-b border-white/10">
          {["pending", "approved", "denied", "all"].map((k) => (
            <button key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${
                tab === k ? "text-[#00E5FF] border-b-2 border-[#00E5FF]"
                          : "text-gray-500 hover:text-white"
              }`}
              data-testid={`admin-refunds-tab-${k}`}>
              {k}
            </button>
          ))}
        </div>

        {busy === "load" ? (
          <div className="mt-6 text-gray-500 font-mono text-sm" data-testid="admin-refunds-loading">
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-6 text-gray-500 font-mono text-sm" data-testid="admin-refunds-empty">
            No {tab === "all" ? "" : tab} refund requests.
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-white/10 bg-white/[0.02] p-4"
                   data-testid={`admin-refunds-row-${r.id}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <CurrencyDollar size={16} className="text-[#00E5FF]" weight="duotone" />
                      <div className="text-white font-bold truncate">
                        MS{r.milestone_idx} · {r.milestone_title || <span className="italic text-gray-500">(untitled)</span>}
                      </div>
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${badge(r.status)}`}
                            data-testid={`admin-refunds-status-${r.id}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="mt-1 text-xs font-mono text-gray-400 truncate">
                      {r.job_title}
                    </div>
                    <div className="mt-3 text-xs text-gray-300 leading-relaxed max-w-2xl whitespace-pre-wrap">
                      <span className="text-gray-500 font-mono">reason · </span>
                      {r.reason}
                    </div>
                    <div className="mt-3 text-[10px] font-mono text-gray-500">
                      requested {new Date(r.created_at).toLocaleString()}
                      {r.decided_at && <> · decided {new Date(r.decided_at).toLocaleString()}</>}
                      {r.admin_note && <> · note · <span className="text-gray-300">{r.admin_note}</span></>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-black text-emerald-400 tabular-nums">
                      ${Number(r.amount_usd || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] font-mono text-gray-500">USD refund</div>
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="mt-4 flex items-center gap-2 pt-3 border-t border-white/5">
                    <button onClick={() => { setDecideModal({ row: r, action: "deny" }); setNote(""); }}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-3 py-2 border border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40"
                      data-testid={`admin-refunds-deny-${r.id}`}>
                      <XCircle size={12} /> deny
                    </button>
                    <button onClick={() => { setDecideModal({ row: r, action: "approve" }); setNote(""); }}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-3 py-2 bg-emerald-400 text-black hover:bg-emerald-400/80 disabled:opacity-40"
                      data-testid={`admin-refunds-approve-${r.id}`}>
                      <CheckCircle size={12} weight="fill" /> approve & refund
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {decideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
             onClick={() => busy !== decideModal.row.id && setDecideModal(null)}
             data-testid="admin-refunds-modal">
          <div className="w-full max-w-md bg-[#0A0C10] border border-white/20 p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              {decideModal.action === "approve"
                ? <CheckCircle size={20} weight="fill" className="text-emerald-400" />
                : <WarningOctagon size={20} weight="fill" className="text-[#FF3B30]" />}
              <div className="label-tag">// {decideModal.action}</div>
            </div>
            <h2 className="font-display text-2xl font-black mt-1">
              {decideModal.action === "approve" ? "Approve refund?" : "Deny request?"}
            </h2>
            <div className="text-xs font-mono text-gray-400 mt-1">
              MS{decideModal.row.milestone_idx} · ${Number(decideModal.row.amount_usd || 0).toLocaleString()} USD
            </div>
            {decideModal.action === "approve" && (
              <div className="mt-4 text-sm text-gray-300 leading-relaxed">
                This will fire the Stripe refund immediately. The PE&apos;s earnings credit will
                be reversed and both sides will be notified.
              </div>
            )}
            <label className="block text-xs font-mono text-gray-400 mt-4">Admin note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3} maxLength={1000}
              placeholder={decideModal.action === "approve"
                ? "Approved on evidence review."
                : "Explain the denial — the client will see this note."}
              className="mt-2 w-full bg-black/50 border border-white/10 focus:border-[#00E5FF] p-3 text-sm text-white outline-none"
              data-testid="admin-refunds-note-input"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setDecideModal(null)}
                disabled={busy === decideModal.row.id}
                className="text-xs font-mono text-gray-400 hover:text-white px-3 py-2"
                data-testid="admin-refunds-modal-cancel">
                cancel
              </button>
              <button onClick={decide}
                disabled={busy === decideModal.row.id}
                className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-4 py-2 ${
                  decideModal.action === "approve"
                    ? "bg-emerald-400 text-black hover:bg-emerald-400/80"
                    : "bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80"
                } disabled:opacity-40`}
                data-testid="admin-refunds-modal-confirm">
                {busy === decideModal.row.id ? "processing…" : `confirm ${decideModal.action}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
